import {
  AP_ACTION_TYPE,
  type ApAgentAction,
  type ApPurchaseDocument,
  type ApPurchaseOrderOption,
} from "@/accounts-payable/types";
import { formatApDate, formatDollars } from "./format";

export const purchaseOrderProposalLink = (
  action: ApAgentAction,
  documentHasJob: boolean,
) => {
  if (action.action_type === AP_ACTION_TYPE.createPo)
    return { label: "Use an existing PO", hasJob: documentHasJob };
  if (action.action_type === AP_ACTION_TYPE.updatePo)
    return { label: "Wrong PO?", hasJob: true };
  return null;
};

/**
 * The ServiceTitan PO a `create_po` made, from the API's record of the apply
 * rather than the payload, which a reviewer can edit while it is pending.
 */
export const createdPurchaseOrderId = (action: ApAgentAction): number | null =>
  action.action_type === AP_ACTION_TYPE.createPo
    ? (action.created_st_id ?? null)
    : null;

export const MIN_PO_SEARCH_LENGTH = 3;

export const purchaseOrderLabel = (option: ApPurchaseOrderOption) =>
  option.number ? `PO ${option.number}` : `PO #${option.st_purchase_order_id}`;

export const purchaseOrderOptionMeta = (option: ApPurchaseOrderOption) =>
  [
    option.status,
    option.st_job_external_id
      ? `Job ${option.job_number ?? `#${option.st_job_external_id}`}`
      : option.st_project_external_id
        ? `Project #${option.st_project_external_id}`
        : "No job",
    option.vendor_name ??
      (option.st_vendor_id ? `Vendor #${option.st_vendor_id}` : null),
    option.total != null ? formatDollars(option.total) : null,
    option.date ? formatApDate(option.date.slice(0, 10)) : null,
  ].filter((part): part is string => !!part);

export const purchaseOrderSearchStatusMessage = ({
  query,
  hasJob,
  isLoading,
  isError,
  resultCount,
}: {
  query: string;
  hasJob: boolean;
  isLoading: boolean;
  isError: boolean;
  resultCount: number;
}): string | null => {
  const q = query.trim();
  if (q.length > 0 && q.length < MIN_PO_SEARCH_LENGTH)
    return `Type at least ${MIN_PO_SEARCH_LENGTH} characters of a PO or job number.`;
  if (isLoading) return "Looking up purchase orders in ServiceTitan…";
  if (isError) return "Couldn’t reach ServiceTitan. Try again.";
  if (resultCount > 0) return null;
  if (q) return "No purchase orders match that PO or job number.";
  return hasJob
    ? "No purchase orders on this job yet. Search by PO or job number."
    : "Search by PO or job number.";
};

export const hasManualPurchaseOrder = (
  document: Pick<ApPurchaseDocument, "po_match_strategy">,
): boolean => document.po_match_strategy === "manual";
